import React from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowRight, MapPin, Sparkles } from 'lucide-react';
import { ALL_CATEGORY_LANDINGS } from '../data/categoryLandingContent';
import { buildLocalizedPath } from './LocaleRouter';
import { useLanguage } from '../context/LanguageContext';
import { useCatalog } from '../context/CatalogContext';

/**
 * Homepage section — "Browse by Category".
 *
 * Data-driven from `src/data/categoryLandingContent.js`. Every entry's
 * `cardIcon`, `cardTagline`, `hero.image` and `label` flow straight onto
 * a premium image card that links to the matching /{category}-…-in-vietnam
 * landing. Adding a new category requires zero edits to this file.
 *
 * Card anatomy: 4:5 background photo + bottom-up navy gradient + glass
 * icon medallion + live treatment-count pill + tagline + CTA.
 *
 * Layout: 2 cols mobile · 3 cols tablet · 4 cols desktop.
 */

/** Safely resolve a lucide-react icon by string name. */
const resolveIcon = (name) => (name && LucideIcons[name]) || Sparkles;

const CategoryDestinationsSection = () => {
  const { language } = useLanguage();
  const { getTreatmentsByCategory } = useCatalog();

  return (
    <section
      className="py-16 lg:py-24 bg-[#F8FAFC] border-t border-slate-100"
      data-testid="category-destinations-section"
    >
      <div className="container mx-auto px-4 max-w-7xl">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-12">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" />
              Browse by Category
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-[2.6rem] font-extrabold text-slate-900 tracking-tight leading-[1.08]">
              Healthcare specialties we coordinate in Vietnam
            </h2>
            <p className="text-slate-500 mt-3 text-base lg:text-lg leading-relaxed">
              Tap a specialty to see the procedures we coordinate, transparent
              USD pricing, and the verified partner clinics behind each.
            </p>
          </div>
          <Link
            to={buildLocalizedPath('/treatments', language)}
            className="hidden lg:inline-flex items-center gap-1.5 text-sm font-bold text-[#1FA7A1] hover:gap-2 transition-all"
            data-testid="browse-categories-see-all"
          >
            See all treatments in Vietnam
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        {/* ── Card grid ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
          {ALL_CATEGORY_LANDINGS.map((c) => {
            const Icon = resolveIcon(c.cardIcon);
            const count = (getTreatmentsByCategory(c.categorySlug) || []).length;
            const tagline = c.cardTagline || `Premium ${c.label.toLowerCase()} in Vietnam`;
            return (
              <Link
                key={c.categorySlug}
                to={buildLocalizedPath(c.urlSlug, language)}
                className="group relative block overflow-hidden rounded-[22px] bg-slate-900 aspect-[4/5] focus:outline-none focus:ring-2 focus:ring-[#1FA7A1] focus:ring-offset-2"
                style={{ boxShadow: '0 8px 28px rgba(15,42,85,0.10)' }}
                data-testid={`category-destination-${c.categorySlug}`}
                aria-label={`${c.label} in Vietnam`}
              >
                <img
                  src={c.hero.image}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-[1.06]"
                  onError={(e) => { if (c.hero.fallback) e.target.src = c.hero.fallback; }}
                />

                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(15,42,85,0.10) 0%, rgba(15,42,85,0.45) 55%, rgba(15,42,85,0.92) 100%)',
                  }}
                />

                <div
                  className="absolute top-4 left-4 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-md"
                  style={{
                    background: 'rgba(255,255,255,0.20)',
                    border: '1px solid rgba(255,255,255,0.35)',
                  }}
                >
                  <Icon className="h-5 w-5 text-white" />
                </div>

                {count > 0 && (
                  <div
                    className="hidden sm:flex absolute top-4 right-4 items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest backdrop-blur-md"
                    style={{
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.30)',
                      color: 'white',
                    }}
                  >
                    {count} treatments
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-0 p-5 lg:p-6 text-white">
                  <h3 className="font-extrabold text-lg lg:text-xl leading-tight tracking-tight">
                    {c.label}
                  </h3>
                  <p className="text-white/85 text-[12.5px] lg:text-sm mt-1.5 leading-snug line-clamp-2">
                    {tagline}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-white group-hover:gap-2.5 transition-all">
                    {c.label} in Vietnam
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </div>
                </div>

                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      'linear-gradient(180deg, transparent 60%, rgba(31,167,161,0.35) 100%)',
                  }}
                />
              </Link>
            );
          })}
        </div>

        <div className="lg:hidden mt-8 text-center">
          <Link
            to={buildLocalizedPath('/treatments', language)}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1FA7A1]"
            data-testid="browse-categories-see-all-mobile"
          >
            See all treatments
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CategoryDestinationsSection;
